import { GOOGLE_MAPS_BROWSER_KEY } from '../config';
import type { PinKind } from '../logic/route';
import { currentTheme } from './theme';

/**
 * Maps JavaScript API glue (M2, D28). Runtime "inline bootstrap" loader (no npm package, loads
 * once per page), classic Markers with inline SVG icons (no Map ID needed), colorScheme follows the app theme (D54).
 * The browser key is public by design — restricted by domain in the Google Cloud console.
 */

let loadPromise: Promise<void> | null = null;
let bootstrapped = false;
let authFailed = false;
const authListeners = new Set<() => void>();

/** Register a callback for Maps auth failures (fires immediately if one already happened).
 *  Returns the unsubscribe a view calls when it is disposed. */
export const onMapsAuthFailure = (cb: () => void): (() => void) => {
  if (authFailed) cb();
  else authListeners.add(cb);
  return () => authListeners.delete(cb);
};

/* eslint-disable */
// Official inline bootstrap (developers.google.com/maps/documentation/javascript/load-maps-js-api),
// injected at runtime. Subsequent calls are no-ops.
const injectBootstrap = (key: string): void => {
  // prettier-ignore
  (g=>{var h:any,a:any,k:any,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b:any=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set<string>(),e=new URLSearchParams(),u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,(t:string)=>"_"+t[0]!.toLowerCase()),(g as any)[k]);e.set("callback",c+".maps."+q);a.src=`https://maps.${c}apis.com/maps/api/js?`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=(m.querySelector("script[nonce]") as HTMLScriptElement|null)?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f:any,...n:any[])=>r.add(f)&&u().then(()=>d[l](f,...n))})({
    key,
    v: 'weekly',
  });
};
/* eslint-enable */

/** Load the Maps JS API once. Rejects on script-load failure, and the next call tries again (a dead
 *  zone must not break Maps until the app restarts); auth failures fire onMapsAuthFailure. */
export const loadGoogleMaps = (): Promise<void> => {
  loadPromise ??= (async () => {
    window.gm_authFailure = () => {
      authFailed = true;
      for (const cb of authListeners) cb();
      authListeners.clear();
    };
    if (!bootstrapped) injectBootstrap(GOOGLE_MAPS_BROWSER_KEY); // its loader retries a failed script itself
    bootstrapped = true;
    await google.maps.importLibrary('maps');
    await google.maps.importLibrary('marker');
    await google.maps.importLibrary('places');
  })().catch((err: unknown) => {
    loadPromise = null;
    throw err;
  });
  return loadPromise;
};

// ---------------------------------------------------------------- pin icons (D25 palette)

const PIN_COLORS: Record<PinKind, string> = {
  competitor: '#4AEDC4', // green/mint — a detected rival POS, hot target
  prospect: '#8fa39b', // gray — unknown or still fingerprinting
  in_pipeline: '#6fa8ff', // blue — already a CRM lead
  hidden_square: '#39544b', // dim — shown only with the "hidden" toggle
  hidden_lost: '#5a4747',
};

const pinSvg = (fill: string, selected: boolean, label?: string): string => {
  const r = selected ? 13 : 9;
  const stroke = selected ? '#ffffff' : '#0B0F0E';
  const text = label
    ? `<text x="16" y="16" fill="#0B0F0E" font-family="system-ui, sans-serif" font-size="12" font-weight="800" text-anchor="middle" dominant-baseline="central">${label}</text>`
    : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${selected ? 3 : 2}"/>${text}
  </svg>`;
};

export const pinIcon = (kind: PinKind, selected: boolean, orderLabel?: string): google.maps.Icon => ({
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pinSvg(PIN_COLORS[kind], selected, orderLabel))}`,
  scaledSize: new google.maps.Size(32, 32),
  anchor: new google.maps.Point(16, 16),
});

export const anchorIcon = (): google.maps.Icon => ({
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
       <path d="M18 3c-6 0-11 4.9-11 11 0 8 11 19 11 19s11-11 11-19c0-6.1-5-11-11-11z" fill="#ffffff" stroke="#0B0F0E" stroke-width="2"/>
       <circle cx="18" cy="14" r="4.5" fill="#0B0F0E"/>
     </svg>`,
  )}`,
  scaledSize: new google.maps.Size(36, 36),
  anchor: new google.maps.Point(18, 33),
});

export const createMap = async (
  el: HTMLElement,
  center: google.maps.LatLngLiteral,
  zoom = 15,
): Promise<google.maps.Map> => {
  await loadGoogleMaps();
  const { Map } = await google.maps.importLibrary('maps');
  return new Map(el, {
    center,
    zoom,
    disableDefaultUI: true,
    gestureHandling: 'greedy',
    colorScheme: currentTheme() === 'light' ? 'LIGHT' : 'DARK', // set at creation; a scout reopens per visit
    clickableIcons: false,
  });
};
